import React, { useState, useMemo } from 'react';
import { X, CheckCircle, Home, Calculator, Euro, TrendingUp, AlertCircle, FileText, Lightbulb, Award, Info, Scale, BookOpen, Users, Clock, ExternalLink } from 'lucide-react';

/**
 * 🏗️ MODERNISIERUNGS-MODAL - MASTER LIVE v1.1
 * 
 * Erweiterte Version mit:
 * - Umfassenden Wissensinhalten
 * - Transparenter "geschätzt"-Kennzeichnung
 * - Detaillierten rechtlichen Grundlagen
 * - Praxis-Tipps und Empfehlungen
 */

const ModernizationModal = ({ 
  isOpen, 
  onClose, 
  tenant, 
  tenantType = 'private', // 'private' oder 'commercial'
  themeClasses 
}) => {
  const [selectedMeasures, setSelectedMeasures] = useState([]);
  const [customCosts, setCustomCosts] = useState('');
  const [fundingAmount, setFundingAmount] = useState('');

  // Modernisierungsmaßnahmen-Katalog mit geschätzten Kosten
  const modernizationMeasures = useMemo(() => [
    {
      id: 'heating',
      name: 'Heizungsmodernisierung',
      description: 'Austausch alter Heizungsanlage gegen moderne, energieeffiziente Technik (Wärmepumpe, Brennwertkessel)',
      detailedDescription: 'Umfasst Demontage der alten Anlage, Installation neuer Heiztechnik, Anschluss an vorhandene Leitungen, hydraulischen Abgleich und Inbetriebnahme',
      estimatedCost: 15000,
      category: 'Energie',
      icon: '🔥',
      eligible: true,
      legalBasis: '§ 559 BGB - Energetische Modernisierung',
      fundingOptions: 'KfW 261/262, BAFA-Förderung bis 70%'
    },
    {
      id: 'insulation',
      name: 'Wärmedämmung',
      description: 'Dämmung von Fassade, Dach oder Kellerdecke zur Energieeinsparung',
      detailedDescription: 'Vollwärmeschutz der Außenfassade, Dachdämmung oder Kellerdeckendämmung inkl. Dampfsperre und Verkleidung',
      estimatedCost: 25000,
      category: 'Energie',
      icon: '🏠',
      eligible: true,
      legalBasis: '§ 559 BGB - Energetische Modernisierung',
      fundingOptions: 'KfW 261, steuerliche Förderung 20%'
    },
    {
      id: 'windows',
      name: 'Fenster & Türen',
      description: 'Austausch gegen energieeffiziente Fenster und Türen (3-fach Verglasung)',
      detailedDescription: 'Kompletter Austausch aller Fenster und Außentüren, inkl. Demontage, Einbau, Abdichtung und Anstrich der Laibungen',
      estimatedCost: 12000,
      category: 'Energie',
      icon: '🪟',
      eligible: true,
      legalBasis: '§ 559 BGB - Energetische Modernisierung',
      fundingOptions: 'KfW 261, BAFA-Einzelmaßnahmen'
    },
    {
      id: 'bathroom',
      name: 'Badmodernisierung',
      description: 'Komplette Sanierung des Badezimmers mit modernen Sanitäranlagen',
      detailedDescription: 'Entkernung, neue Fliesen, Sanitärobjekte, Armaturen, Beleuchtung und barrierefreie Ausstattung',
      estimatedCost: 18000,
      category: 'Komfort',
      icon: '🛁',
      eligible: tenantType === 'private',
      legalBasis: '§ 559 BGB - Gebrauchswerterhöhung',
      fundingOptions: 'KfW 455-B Altersgerecht Umbauen'
    },
    {
      id: 'kitchen',
      name: 'Küchenmodernisierung',
      description: 'Neue Einbauküche oder umfassende Küchenmodernisierung',
      detailedDescription: 'Komplette Küchenzeile mit Elektrogeräten, Arbeitsplatten, Spüle und energieeffizienten Geräten',
      estimatedCost: 20000,
      category: 'Komfort',
      icon: '🍳',
      eligible: tenantType === 'private',
      legalBasis: '§ 559 BGB - Gebrauchswerterhöhung',
      fundingOptions: 'Keine spezifische Förderung'
    },
    {
      id: 'elevator',
      name: 'Aufzug',
      description: 'Einbau oder Modernisierung eines Aufzugs für Barrierefreiheit',
      detailedDescription: 'Kompletter Aufzugseinbau inkl. Schacht, Technik, TÜV-Abnahme und laufende Wartung',
      estimatedCost: 80000,
      category: 'Barrierefreiheit',
      icon: '🛗',
      eligible: true,
      legalBasis: '§ 559 BGB - Gebrauchswerterhöhung',
      fundingOptions: 'KfW 455-B, regionale Förderprogramme'
    }
  ], [tenantType]);

  // Toggle selected measures
  const toggleMeasure = (measure) => {
    setSelectedMeasures(prev => {
      const exists = prev.find(m => m.id === measure.id);
      if (exists) {
        return prev.filter(m => m.id !== measure.id);
      } else {
        return [...prev, measure];
      }
    });
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Format percentage
  const formatPercentage = (value) => {
    return `${value.toFixed(1)}%`;
  };

  // Berechnung der umlegbaren Kosten
  const calculationResult = useMemo(() => {
    const totalCosts = selectedMeasures.reduce((sum, measure) => sum + measure.estimatedCost, 0) + 
                      (parseFloat(customCosts) || 0);
    const fundingDeduction = parseFloat(fundingAmount) || 0;
    const netCosts = Math.max(0, totalCosts - fundingDeduction);
    
    // 8%-Regel für Wohnraum, 12% für Gewerbe
    const maxAnnualIncrease = tenantType === 'private' ? netCosts * 0.08 : netCosts * 0.12;
    const monthlyIncrease = maxAnnualIncrease / 12;
    
    const currentRent = tenant?.monthly_rent || 0;
    const newRent = currentRent + monthlyIncrease;
    const percentageIncrease = currentRent > 0 ? (monthlyIncrease / currentRent) * 100 : 0;
    
    return {
      totalCosts,
      fundingDeduction,
      netCosts,
      maxAnnualIncrease,
      monthlyIncrease,
      currentRent,
      newRent,
      percentageIncrease
    };
  }, [selectedMeasures, customCosts, fundingAmount, tenant, tenantType]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className={`${themeClasses.cardBg} rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden`}>
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className={`text-xl font-bold ${themeClasses.text} flex items-center`}>
            <Home className="w-6 h-6 mr-3 text-green-600" />
            Modernisierungsanalyse - {tenant?.name || 'Mieter'}
            <span className="ml-2 px-2 py-1 text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded-full">
              Master Live v1.1
            </span>
          </h3>
          <button
            onClick={onClose}
            className={`${themeClasses.textSecondary} hover:${themeClasses.text} transition-colors`}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex h-[calc(90vh-120px)]">
          {/* Hauptbereich (2/3) */}
          <div className="flex-1 p-6 overflow-y-auto">
            
            {/* Mieter-Info */}
            <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
              <div>
                <p className={`text-sm font-medium ${themeClasses.textSecondary}`}>Mieter</p>
                <p className={`font-bold ${themeClasses.text}`}>{tenant?.name || 'Unbekannt'}</p>
              </div>
              <div>
                <p className={`text-sm font-medium ${themeClasses.textSecondary}`}>Aktuelle Miete</p>
                <p className={`font-bold ${themeClasses.text}`}>{formatCurrency(tenant?.monthly_rent || 0)}</p>
              </div>
              <div>
                <p className={`text-sm font-medium ${themeClasses.textSecondary}`}>Typ</p>
                <p className={`font-bold ${themeClasses.text}`}>
                  {tenantType === 'private' ? 'Wohnraum' : 'Gewerbe'}
                  <span className={`ml-2 text-xs px-2 py-1 rounded-full ${
                    tenantType === 'private' 
                      ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                      : 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                  }`}>
                    {tenantType === 'private' ? '8%-Regel' : '12%-Regel'}
                  </span>
                </p>
              </div>
            </div>

            {/* Modernisierungsmaßnahmen */}
            <div className="mb-6">
              <h4 className={`font-medium ${themeClasses.text} mb-4 flex items-center`}>
                <Calculator className="w-4 h-4 mr-2 text-green-600" />
                Modernisierungsmaßnahmen auswählen
                <span className="ml-2 text-xs text-orange-600">*geschätzte Kosten</span>
              </h4>
              
              <div className="grid grid-cols-1 gap-4">
                {modernizationMeasures.filter(m => m.eligible).map(measure => (
                  <div
                    key={measure.id}
                    onClick={() => toggleMeasure(measure)}
                    className={`p-4 rounded-lg border cursor-pointer transition-all ${
                      selectedMeasures.find(m => m.id === measure.id)
                        ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-green-300'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <span className="text-2xl">{measure.icon}</span>
                          <div>
                            <p className={`font-bold ${themeClasses.text}`}>{measure.name}</p>
                            <p className={`text-sm ${themeClasses.textSecondary}`}>
                              {measure.description}
                            </p>
                          </div>
                        </div>
                        
                        <div className="ml-11 space-y-2">
                          <p className={`text-sm ${themeClasses.textSecondary}`}>
                            <strong>Arbeiten:</strong> {measure.detailedDescription}
                          </p>
                          <div className="flex items-center space-x-4 text-xs">
                            <span className={`px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-800 ${themeClasses.textSecondary}`}>
                              {measure.category}
                            </span>
                            <span className="text-blue-600">
                              {measure.legalBasis}
                            </span>
                            <span className="text-green-600">
                              {measure.fundingOptions}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right ml-4">
                        <span className={`text-lg font-bold text-green-600`}>
                          {formatCurrency(measure.estimatedCost)}*
                        </span>
                        <p className="text-xs text-orange-600">geschätzt</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Zusätzliche Kosten & Förderung */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className={`block text-sm font-medium ${themeClasses.text} mb-2`}>
                  Zusätzliche Kosten (€)*
                </label>
                <input
                  type="number"
                  value={customCosts}
                  onChange={(e) => setCustomCosts(e.target.value)}
                  placeholder="0"
                  className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg ${themeClasses.input}`}
                />
                <p className="text-xs text-orange-600 mt-1">*geschätzte Zusatzkosten</p>
              </div>
              <div>
                <label className={`block text-sm font-medium ${themeClasses.text} mb-2`}>
                  Förderung/Zuschüsse (€)
                </label>
                <input
                  type="number"
                  value={fundingAmount}
                  onChange={(e) => setFundingAmount(e.target.value)}
                  placeholder="0"
                  className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg ${themeClasses.input}`}
                />
                <p className="text-xs text-blue-600 mt-1">erwartete Förderung</p>
              </div>
            </div>

            {/* Berechnung */}
            <div className={`bg-green-50 dark:bg-green-900/20 rounded-lg p-4 mb-6`}>
              <h4 className={`font-medium ${themeClasses.text} mb-3 flex items-center`}>
                <TrendingUp className="w-4 h-4 mr-2 text-green-600" />
                Modernisierungs-Berechnung (geschätzt)
              </h4>
              
              <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                <div>
                  <p className={`font-medium ${themeClasses.text}`}>Gesamtkosten*:</p>
                  <p className={`text-lg font-bold ${themeClasses.text}`}>
                    {formatCurrency(calculationResult.totalCosts)}
                  </p>
                </div>
                <div>
                  <p className={`font-medium ${themeClasses.text}`}>Abzgl. Förderung:</p>
                  <p className={`text-lg font-bold text-blue-600`}>
                    -{formatCurrency(calculationResult.fundingDeduction)}
                  </p>
                </div>
                <div>
                  <p className={`font-medium ${themeClasses.text}`}>Umlegbare Kosten:</p>
                  <p className={`text-lg font-bold ${themeClasses.text}`}>
                    {formatCurrency(calculationResult.netCosts)}
                  </p>
                </div>
                <div>
                  <p className={`font-medium ${themeClasses.text}`}>
                    {tenantType === 'private' ? '8%-Regel (jährlich):' : '12%-Regel (jährlich):'}
                  </p>
                  <p className={`text-lg font-bold text-green-600`}>
                    {formatCurrency(calculationResult.maxAnnualIncrease)}
                  </p>
                </div>
              </div>

              <div className="border-t border-green-200 dark:border-green-700 pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`font-medium ${themeClasses.text}`}>Mögliche Mieterhöhung:</p>
                    <p className={`text-sm ${themeClasses.textSecondary}`}>
                      {formatCurrency(calculationResult.monthlyIncrease)} monatlich
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={`text-2xl font-bold text-green-600`}>
                      +{formatPercentage(calculationResult.percentageIncrease)}
                    </p>
                    <p className={`text-sm ${themeClasses.textSecondary}`}>
                      Neue Miete: {formatCurrency(calculationResult.newRent)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Rechtliche Hinweise (nur bei Wohnraum) */}
            {tenantType === 'private' && (
              <div className={`bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4 mb-6`}>
                <div className="flex items-start space-x-2">
                  <AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className={`text-sm font-medium ${themeClasses.text}`}>Rechtliche Grenzen (Wohnraum):</p>
                    <ul className={`text-sm ${themeClasses.textSecondary} mt-1 space-y-1`}>
                      <li>• Modernisierungsumlage max. 8% der Kosten pro Jahr (§ 559 BGB)</li>
                      <li>• Kappungsgrenze: Max. 3€/m² in 6 Jahren (§ 559a BGB)</li>
                      <li>• Ankündigungsfrist: 3 Monate vor Beginn der Maßnahme</li>
                      <li>• Mieterhöhung frühestens nach Abschluss der Modernisierung</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Berechnungsweg */}
            <div className={`bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4`}>
              <h4 className={`font-medium ${themeClasses.text} mb-3 flex items-center`}>
                <FileText className="w-4 h-4 mr-2 text-gray-600" />
                Berechnungsweg (geschätzt)
              </h4>
              <div className={`text-sm ${themeClasses.textSecondary} space-y-1`}>
                <p>1. Gesamtkosten*: {formatCurrency(calculationResult.totalCosts)}</p>
                <p>2. Abzgl. Förderung: -{formatCurrency(calculationResult.fundingDeduction)}</p>
                <p>3. Umlegbare Kosten: {formatCurrency(calculationResult.netCosts)}</p>
                <p>4. {tenantType === 'private' ? '8%-Regel' : '12%-Regel'}: {formatCurrency(calculationResult.netCosts)} × {tenantType === 'private' ? '8%' : '12%'} = {formatCurrency(calculationResult.maxAnnualIncrease)}</p>
                <p>5. Monatliche Erhöhung: {formatCurrency(calculationResult.maxAnnualIncrease)} ÷ 12 = {formatCurrency(calculationResult.monthlyIncrease)}</p>
              </div>
              <p className="text-xs text-orange-600 mt-2">*Alle Kostenangaben sind Schätzwerte - konkrete Angebote erforderlich</p>
            </div>
          </div>

          {/* Erweiterte Wissens-Sidebar (1/3) */}
          <div className="w-96 bg-gray-50 dark:bg-gray-800/50 p-6 overflow-y-auto border-l border-gray-200 dark:border-gray-700">
            
            {/* Modernisierungsumlage Grundlagen */}
            <div className="mb-6">
              <h4 className={`font-medium ${themeClasses.text} mb-3 flex items-center`}>
                <Scale className="w-4 h-4 mr-2 text-blue-600" />
                Rechtliche Grundlagen
              </h4>
              <div className={`text-sm ${themeClasses.textSecondary} space-y-3`}>
                <div>
                  <p className={`font-medium ${themeClasses.text}`}>§ 559 BGB - Modernisierungsumlage</p>
                  <p>Vermieter können jährlich bis zu 8% (Wohnen) bzw. 12% (Gewerbe) der Modernisierungskosten auf die Miete umlegen. Voraussetzung: nachhaltige Erhöhung des Gebrauchswerts, Verbesserung der Wohnverhältnisse oder Energieeinsparung.</p>
                </div>
                <div>
                  <p className={`font-medium ${themeClasses.text}`}>§ 559a BGB - Kappungsgrenze</p>
                  <p>Bei Wohnraum: Die Miete darf durch Modernisierung in 6 Jahren um höchstens 3€/m² oder 2€/m² (in Gebieten mit angespanntem Wohnungsmarkt) steigen.</p>
                </div>
                <div>
                  <p className={`font-medium ${themeClasses.text}`}>§ 555b BGB - Ankündigung</p>
                  <p>Modernisierungsmaßnahmen müssen dem Mieter spätestens 3 Monate vor Beginn schriftlich angekündigt werden. Die Ankündigung muss Art, Umfang, Beginn und Dauer der Arbeiten sowie die zu erwartende Mieterhöhung enthalten.</p>
                </div>
              </div>
            </div>

            {/* Arten der Modernisierung */}
            <div className="mb-6">
              <h4 className={`font-medium ${themeClasses.text} mb-3 flex items-center`}>
                <BookOpen className="w-4 h-4 mr-2 text-green-600" />
                Modernisierungsarten
              </h4>
              <div className={`text-sm ${themeClasses.textSecondary} space-y-3`}>
                <div>
                  <p className={`font-medium ${themeClasses.text}`}>• Energetische Modernisierung</p>
                  <p>Maßnahmen zur Energieeinsparung (Dämmung, Heizung, Fenster). Besonders förderungswürdig und steuerlich absetzbar. Oft 50-70% Förderung möglich.</p>
                </div>
                <div>
                  <p className={`font-medium ${themeClasses.text}`}>• Gebrauchswerterhöhung</p>
                  <p>Verbesserung der Ausstattung (Bad, Küche, Balkon). Erhöht den Wohnkomfort und damit den Mietwert nachhaltig.</p>
                </div>
                <div>
                  <p className={`font-medium ${themeClasses.text}`}>• Barrierefreiheit</p>
                  <p>Maßnahmen für altersgerechtes Wohnen (Aufzug, schwellenfreie Zugänge). KfW-Förderung bis 6.250€ pro Wohneinheit möglich.</p>
                </div>
                <div>
                  <p className={`font-medium ${themeClasses.text}`}>• Neubau von Wohnraum</p>
                  <p>Dachgeschossausbau, Anbauten. Schafft zusätzlichen Wohnraum und ist vollständig umlagefähig.</p>
                </div>
              </div>
            </div>

            {/* Förderungen im Detail */}
            <div className="mb-6">
              <h4 className={`font-medium ${themeClasses.text} mb-3 flex items-center`}>
                <Award className="w-4 h-4 mr-2 text-green-600" />
                Förderungen & Zuschüsse
              </h4>
              <div className={`text-sm ${themeClasses.textSecondary} space-y-3`}>
                <div>
                  <p className={`font-medium ${themeClasses.text}`}>• KfW 261/262 - Wohngebäude</p>
                  <p>Kredit bis 150.000€ mit Tilgungszuschuss bis 37.500€ für Sanierung zum Effizienzhaus. Zinssatz ab 0,01% effektiv.</p>
                </div>
                <div>
                  <p className={`font-medium ${themeClasses.text}`}>• BAFA - Heizungsförderung</p>
                  <p>Bis zu 70% Förderung für Wärmepumpen, 20% für Gasheizungen. Zusätzlich 10% Heizungs-Tausch-Bonus möglich.</p>
                </div>
                <div>
                  <p className={`font-medium ${themeClasses.text}`}>• Steuerliche Förderung</p>
                  <p>20% der Kosten über 3 Jahre von der Steuer absetzen (max. 40.000€ pro Objekt). Alternative zur direkten Förderung.</p>
                </div>
                <div>
                  <p className={`font-medium ${themeClasses.text}`}>• KfW 455-B - Altersgerecht</p>
                  <p>Zuschuss bis 6.250€ pro Wohneinheit für Barrierereduzierung. Kombination mit anderen Programmen möglich.</p>
                </div>
                <div>
                  <p className={`font-medium ${themeClasses.text}`}>• Regionale Programme</p>
                  <p>Viele Bundesländer und Kommunen bieten zusätzliche Förderprogramme. Prüfung vor Maßnahmenbeginn empfohlen.</p>
                </div>
              </div>
            </div>

            {/* Praxis-Tipps */}
            <div className="mb-6">
              <h4 className={`font-medium ${themeClasses.text} mb-3 flex items-center`}>
                <Lightbulb className="w-4 h-4 mr-2 text-yellow-600" />
                Praxis-Tipps
              </h4>
              <div className={`text-sm ${themeClasses.textSecondary} space-y-3`}>
                <div>
                  <p className={`font-medium ${themeClasses.text}`}>• Energieberatung nutzen</p>
                  <p>Vor größeren Maßnahmen professionelle Energieberatung in Anspruch nehmen. Wird mit bis zu 80% gefördert und ist Voraussetzung für viele Förderprogramme.</p>
                </div>
                <div>
                  <p className={`font-medium ${themeClasses.text}`}>• Mieter früh einbeziehen</p>
                  <p>Transparente Kommunikation über geplante Maßnahmen, Kosten und Nutzen. Reduziert Widerstand und rechtliche Risiken.</p>
                </div>
                <div>
                  <p className={`font-medium ${themeClasses.text}`}>• Gesamtkonzept entwickeln</p>
                  <p>Einzelmaßnahmen aufeinander abstimmen. Oft sind Kombinationen wirtschaftlicher und technisch sinnvoller.</p>
                </div>
                <div>
                  <p className={`font-medium ${themeClasses.text}`}>• Qualifizierte Handwerker</p>
                  <p>Nur zertifizierte Fachbetriebe beauftragen. Viele Förderungen setzen dies voraus und Qualität ist entscheidend für Langzeiterfolg.</p>
                </div>
              </div>
            </div>

            {/* Zeitplanung */}
            <div className="mb-6">
              <h4 className={`font-medium ${themeClasses.text} mb-3 flex items-center`}>
                <Clock className="w-4 h-4 mr-2 text-purple-600" />
                Zeitplanung & Ablauf
              </h4>
              <div className={`text-sm ${themeClasses.textSecondary} space-y-2`}>
                <div className="flex items-start space-x-2">
                  <span className="font-bold text-purple-600">1.</span>
                  <div>
                    <p className={`font-medium ${themeClasses.text}`}>Planung & Beratung (2-4 Monate)</p>
                    <p>Energieberatung, Angebote einholen, Förderanträge stellen</p>
                  </div>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="font-bold text-purple-600">2.</span>
                  <div>
                    <p className={`font-medium ${themeClasses.text}`}>Mieter-Ankündigung (3 Monate vorher)</p>
                    <p>Schriftliche Modernisierungsankündigung mit allen Details</p>
                  </div>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="font-bold text-purple-600">3.</span>
                  <div>
                    <p className={`font-medium ${themeClasses.text}`}>Durchführung (je nach Umfang)</p>
                    <p>Koordinierte Ausführung aller Gewerke</p>
                  </div>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="font-bold text-purple-600">4.</span>
                  <div>
                    <p className={`font-medium ${themeClasses.text}`}>Abrechnung & Mieterhöhung</p>
                    <p>Nach Fertigstellung: Kostenabrechnung und Mieterhöhung</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Mieter-Rechte */}
            <div className="mb-6">
              <h4 className={`font-medium ${themeClasses.text} mb-3 flex items-center`}>
                <Users className="w-4 h-4 mr-2 text-red-600" />
                Mieter-Rechte & Pflichten
              </h4>
              <div className={`text-sm ${themeClasses.textSecondary} space-y-3`}>
                <div>
                  <p className={`font-medium ${themeClasses.text}`}>• Duldungspflicht</p>
                  <p>Mieter müssen Modernisierungsmaßnahmen grundsätzlich dulden, außer bei unzumutbarer Härte (§ 555d BGB).</p>
                </div>
                <div>
                  <p className={`font-medium ${themeClasses.text}`}>• Mietminderung</p>
                  <p>Während der Bauzeit können Mieter die Miete angemessen mindern (meist 10-25% je nach Beeinträchtigung).</p>
                </div>
                <div>
                  <p className={`font-medium ${themeClasses.text}`}>• Widerspruchsrecht</p>
                  <p>Mieter können binnen 2 Monaten Widerspruch gegen die Mieterhöhung einlegen und Belege verlangen.</p>
                </div>
                <div>
                  <p className={`font-medium ${themeClasses.text}`}>• Sonderkündigungsrecht</p>
                  <p>Bei Mieterhöhung über 3€/m² oder mehr als 10% können Mieter außerordentlich kündigen.</p>
                </div>
              </div>
            </div>

            {/* Quellen & Links */}
            <div>
              <h4 className={`font-medium ${themeClasses.text} mb-3 flex items-center`}>
                <ExternalLink className="w-4 h-4 mr-2 text-gray-600" />
                Quellen & weiterführende Links
              </h4>
              <div className={`text-xs ${themeClasses.textSecondary} space-y-2`}>
                <a href="https://www.gesetze-im-internet.de/bgb/__559.html" target="_blank" rel="noopener noreferrer" 
                   className="block text-blue-600 hover:text-blue-800 transition-colors">
                  § 559 BGB - Modernisierungsumlage
                </a>
                <a href="https://www.gesetze-im-internet.de/bgb/__559a.html" target="_blank" rel="noopener noreferrer" 
                   className="block text-blue-600 hover:text-blue-800 transition-colors">
                  § 559a BGB - Kappungsgrenze
                </a>
                <a href="https://www.kfw.de/inlandsfoerderung/Privatpersonen/Bestehende-Immobilie/" target="_blank" rel="noopener noreferrer" 
                   className="block text-blue-600 hover:text-blue-800 transition-colors">
                  KfW-Förderung Gebäudesanierung
                </a>
                <a href="https://www.bafa.de/DE/Energie/Effiziente_Gebaeude/effiziente_gebaeude_node.html" target="_blank" rel="noopener noreferrer" 
                   className="block text-blue-600 hover:text-blue-800 transition-colors">
                  BAFA-Förderung Heizung & Gebäude
                </a>
                <a href="https://www.bundesfinanzhof.de/" target="_blank" rel="noopener noreferrer" 
                   className="block text-blue-600 hover:text-blue-800 transition-colors">
                  Steuerliche Behandlung von Modernisierungen
                </a>
                <a href="https://www.bmwsb.bund.de/Webs/BMWSB/DE/themen/stadt-wohnen/wohnraumfoerderung/energieeffizient-sanieren/energieeffizient-sanieren-node.html" target="_blank" rel="noopener noreferrer" 
                   className="block text-blue-600 hover:text-blue-800 transition-colors">
                  BMWSB - Energieeffizient Sanieren
                </a>
                <a href="https://www.verbraucherzentrale.de/wissen/energie/energetische-sanierung" target="_blank" rel="noopener noreferrer" 
                   className="block text-blue-600 hover:text-blue-800 transition-colors">
                  Verbraucherzentrale - Energetische Sanierung
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className={`px-6 py-2 ${themeClasses.buttonSecondary} ${themeClasses.text} rounded-lg font-medium`}
          >
            Schließen
          </button>
          <button
            onClick={() => {
              // TODO: Implementierung der Modernisierungs-Umsetzung
              console.log('Modernisierung validieren & umsetzen', calculationResult);
              alert('Modernisierungs-Planung gespeichert! Konkrete Angebote erforderlich für finale Umsetzung.');
            }}
            className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium flex items-center"
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Planung speichern
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModernizationModal;

